/* Copyright (c) 2022-2023, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Observable, of, ReplaySubject, Subject, throwError, defer } from 'rxjs';
import { tap, catchError, take, concatMap, takeUntil } from 'rxjs/operators';
import {
    Action,
    State,
    StateMachineError,
    StateMachineErrorCodes,
    StateMachineMetadata,
    States,
    Transitions,
    WildcardState
} from './state-machine.models';

interface SerializerData<T> {
    action: Action;
    stateData: T;
    response: ReplaySubject<T>;
}

export class StateMachine<T extends object> {
    private _tag: string;
    private _states: States<T>;
    private _transitions: Transitions;
    private _startState: State;
    private _endState: State;

    private _currentState: State;
    get currentState(): State {
        return this._currentState;
    }

    private _serializer = new Subject<SerializerData<T>>();
    private _reset = new Subject<void>();

    /**
     * @param metadata State Machine metadata which defines the states and transitions between them.
     *
     * @param validate Perform additional validation at the time of construction.
     *
     * This checks for the following errors/bugs:
     * 1. Transitions defined from end state.
     * 2. Invalid state names found in transition definitions.
     * 3. Ambiguous transitions (one action has multiple transitions from the same current state).
     * 4. Unreachable states.
     * 5. Multiple transitions defined on an action which already has a wildcard transition definition.
     */
    constructor(metadata: StateMachineMetadata<T>, validate?: boolean) {
        this.setupStateMachine(metadata);
        if (validate) {
            this.validateStateMachine();
        }
        this.setupSerializer();
    }

    /**
     * @description Perform an `Action` on the state machine to transition to a different state.
     * If the action cannot be performed on the current state, an error will be thrown.
     * Performs the following operations in this specific order for a valid action:
     * 1. Execute `onExit()` of the current state with the provided state data.
     * 2. Advance `StateMachine.currentState` to the next state.
     * 3. Execute `onEnter()` of the new state with the result of previous state's `onExit()`
     *
     * @throws {StateMachineError} if:
     * 1. Attempting to transition from `endState`.
     * 2. No valid transition found from current state on `Action`.
     * 3. `onExit()` or `onEnter()` functions throw an error while executing.
     */
    on(action: Action, stateData: T): Observable<T> {
        return defer(() => {
            const request: SerializerData<T> = {
                action,
                stateData,
                response: new ReplaySubject<T>(1)
            };
            this._serializer.next(request);
            return request.response.asObservable();
        });
    }

    /**
     * @description Resets the state machine without processing any further transitions.
     * Existing subscribers to `StateMachine.on()` will be invalid after this point.
     * Current state will be reset to the `startState` provided in the `metadata`.
     */
    reset(): void {
        this._reset.next();
        this._currentState = this._startState;
        this.setupSerializer();
    }

    private executeTransition(action: Action, stateData: T): Observable<T> {
        if (this._currentState === this._endState) {
            return throwError(
                this.createStateMachineError(
                    StateMachineErrorCodes.TransitionFromEndState,
                    'Cannot transition from end state'
                )
            );
        }
        const nextStateName = this._transitions[action]?.find(
            transition => transition.from === this._currentState || transition.from === WildcardState
        )?.to;
        if (!nextStateName) {
            return throwError(
                this.createStateMachineError(
                    StateMachineErrorCodes.TransitionNotFound,
                    `Could not find a valid targetState from currentState ${this.currentState} on action ${action}`
                )
            );
        }
        const currentState = this._states[this._currentState];
        const nextState = this._states[nextStateName];
        const onCurrentStateExit = currentState?.onExit ?? (x => of(x));
        const onNextStateEnter = nextState?.onEnter ?? (x => of(x));

        return onCurrentStateExit(stateData).pipe(
            take(1),
            tap(() => (this._currentState = nextStateName)),
            concatMap(previousStateExitData => onNextStateEnter(previousStateExitData)),
            take(1),
            catchError(error =>
                throwError(
                    this.createStateMachineError(
                        StateMachineErrorCodes.TransitionError,
                        `Error transitioning to ${nextStateName}`,
                        error
                    )
                )
            )
        );
    }

    private setupStateMachine(metadata: StateMachineMetadata<T>) {
        this._tag = metadata.tag;

        this._states = metadata.states;
        if (!this._states) {
            throw this.createStateMachineError(StateMachineErrorCodes.InvalidStates, 'State list cannot be nullish');
        }

        this._transitions = metadata.transitions;
        if (!this._transitions) {
            throw this.createStateMachineError(
                StateMachineErrorCodes.InvalidTransitions,
                'Transitions list cannot be nullish'
            );
        }

        this._startState = Object.keys(this._states).find(state => state === metadata.startState);
        if (!this._startState) {
            throw this.createStateMachineError(
                StateMachineErrorCodes.InvalidStartState,
                `Could not find valid start state with name ${metadata.startState}`
            );
        }

        this._endState = Object.keys(this._states).find(state => state === metadata.endState);
        if (!this._endState) {
            throw this.createStateMachineError(
                StateMachineErrorCodes.InvalidEndState,
                `Could not find valid end state with name ${metadata.endState}`
            );
        }

        if (Object.keys(this._states).includes(WildcardState)) {
            throw this.createStateMachineError(
                StateMachineErrorCodes.InvalidWildcardState,
                'Wildcard state name cannot be specified manually in state definitions'
            );
        }

        this._currentState = metadata.startState;
    }

    private setupSerializer(): void {
        this._serializer
            .pipe(
                concatMap(data =>
                    this.executeTransition(data.action, data.stateData).pipe(
                        concatMap(result => {
                            data.response.next(result);
                            data.response.complete();
                            return of(result);
                        }),
                        catchError(error => {
                            data.response.error(error);
                            return of(error);
                        })
                    )
                ),
                takeUntil(this._reset)
            )
            .subscribe();
    }

    private createStateMachineError(code: StateMachineErrorCodes, message: string, data?: any): StateMachineError {
        return new StateMachineError(this._tag, code, message, data);
    }

    private validateStateMachine(): void {
        const reachedStates: Map<State, boolean> = new Map();
        Object.keys(this._states).forEach(state => reachedStates.set(state, false));
        reachedStates.set(this._startState, true);

        Object.keys(this._transitions).forEach(act => {
            const transitionCount: Map<State, number> = new Map();
            Object.keys(this._states).forEach(state => transitionCount.set(state, 0));

            this._transitions[act].forEach(tran => {
                // transitions from end states
                if (tran.from === this._endState) {
                    throw this.createStateMachineError(
                        StateMachineErrorCodes.TransitionFromEndState,
                        'Cannot transition from end state'
                    );
                }

                // wildcard state specified in target
                if (tran.to === WildcardState) {
                    throw this.createStateMachineError(
                        StateMachineErrorCodes.WildcardTargetState,
                        `Wildcard state cannot be targetState on action ${act}`
                    );
                }

                // multiple wildcard transitions
                if (tran.from === WildcardState && this._transitions[act].length !== 1) {
                    throw this.createStateMachineError(
                        StateMachineErrorCodes.MultipleWildcardTransitions,
                        `Cannot specify multiple transitions on action ${act} if one of them is a Wildcard`
                    );
                }

                // invalid state name in currentState of transitions (Wildcard allowed)
                if (![...Object.keys(this._states), WildcardState].includes(tran.from)) {
                    throw this.createStateMachineError(
                        StateMachineErrorCodes.InvalidStateName,
                        `Invalid currentState ${tran.from} on action ${act} to state ${tran.to}`
                    );
                }

                // invalid state name in targetState of transitions
                if (!Object.keys(this._states).includes(tran.to)) {
                    throw this.createStateMachineError(
                        StateMachineErrorCodes.InvalidStateName,
                        `Invalid targetState ${tran.to} on action ${act} from state ${tran.from}`
                    );
                }

                // ambiguous transitions (multiple transitions on the same action from the same currentState)
                transitionCount.set(tran.from, transitionCount.get(tran.from) + 1);

                // unreachable states
                reachedStates.set(tran.to, true);
            });

            transitionCount.forEach((count, state) => {
                if (count > 1) {
                    throw this.createStateMachineError(
                        StateMachineErrorCodes.AmbiguousTransition,
                        `Ambiguous transition on action ${act} from state ${state}`
                    );
                }
            });
        });

        reachedStates.forEach((reached, state) => {
            if (!reached) {
                throw this.createStateMachineError(
                    StateMachineErrorCodes.UnreachableState,
                    `State ${state} is unreachable`
                );
            }
        });
    }
}
